import classNames from "classnames";
import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTimelineCtx } from "../ctx";
import { useTimelineItemDetails } from "../hooks";
import { useTimelineItemAnim } from "../hooks/use-timeline-item-anim";
import { TimelineContentWrappingEnum } from "../types";
import { TimelineHorizontalTrackSegment } from "./timeline-horizontal-track-segment";
import { TimelineMarkerFactory } from "./timeline-marker-factory";
import { TimelineVerticalTrackSegment } from "./timeline-vertical-track-segment";

const maxHeightInRange = (
  heights: Map<number, number>,
  count: number,
): number | null => {
  let max: number | null = null;
  for (const [idx, height] of Array.from(heights)) {
    if (idx >= count) continue;
    if (max === null || height > max) max = height;
  }
  return max;
};

const splitMiddleCardVars = (
  setSettings: Record<string, unknown>,
): Record<string, string> => {
  const vars: Record<string, string> = {};
  const bg = setSettings.backgroundColor as string | undefined;
  const borderColor = setSettings.borderColor as string | undefined;
  const borderWidth = setSettings.borderWidth as number | undefined;
  const round = setSettings.round as string | undefined;
  if (bg) vars["--tl-card-bg"] = bg;
  if (borderColor && typeof borderWidth === "number" && borderWidth > 0) {
    vars["--tl-card-border"] = `${borderWidth}px solid ${borderColor}`;
  }
  if (round) vars["--tl-card-radius"] = round;
  return vars;
};

interface IProps {
  setSettings: Record<string, unknown>;

  children: React.ReactNode;
  itemIdx: number;

  isDragged?: boolean;
  editableMarkerSlot?: ReactNode;
  editableTextSlot?: ReactNode;
}

export const TimelineItem = ({
  setSettings,
  children,
  itemIdx,
  isDragged,
  editableMarkerSlot,
  editableTextSlot,
}: IProps) => {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const splitMiddleRef = useRef<HTMLDivElement | null>(null);

  const {
    isVertical,
    isHorizontal,
    moveToItemIdx,
    isMobile,
    isPdf,
    comboSettings,
    totalAmtOfItems,
    pos0Heights,
    registerPos0Height,
    pos1Heights,
    registerPos1Height,
  } = useTimelineCtx();

  // Split-middle / wide-middle: measure pos-0 / pos-1 / track heights so
  // pos-0 and pos-1 can be pinned to the max group height, keeping the
  // track line vertically centered across every item. Layout-affecting
  // settings are in the deps below so the ResizeObserver re-attaches
  // when the user toggles INTO this branch.
  const [trackMaxHeight, setTrackMaxHeight] = useState(0);
  const [lineTop, setLineTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    const wrapper = splitMiddleRef.current;
    if (!wrapper) return;
    const pos0 = wrapper.querySelector<HTMLElement>(".group.pos-0");
    const pos1 = wrapper.querySelector<HTMLElement>(".group.pos-1");
    const track = wrapper.querySelector<HTMLElement>(".timeline-item-track");
    if (!pos0) return;
    const update = () => {
      pos0.style.setProperty("min-height", "0", "important");
      if (pos1) pos1.style.setProperty("min-height", "0", "important");
      const pos0NaturalHeight = pos0.getBoundingClientRect().height;
      const pos1NaturalHeight = pos1
        ? pos1.getBoundingClientRect().height
        : null;
      pos0.style.removeProperty("min-height");
      if (pos1) pos1.style.removeProperty("min-height");

      registerPos0Height(itemIdx, pos0NaturalHeight);
      if (pos1 && pos1NaturalHeight !== null) {
        registerPos1Height(itemIdx, pos1NaturalHeight);
      }

      const wrapperTop = wrapper.getBoundingClientRect().top;
      const pos0Rect = pos0.getBoundingClientRect();
      if (pos1) {
        const pos1Rect = pos1.getBoundingClientRect();
        setLineTop((pos0Rect.bottom + pos1Rect.top) / 2 - wrapperTop);
      } else {
        setLineTop(pos0Rect.bottom - wrapperTop);
      }
      if (track) {
        const h = track.getBoundingClientRect().height;
        setTrackMaxHeight((prev) => (h > prev ? h : prev));
      }
    };
    let rafId = 0;
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };

    update();

    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(pos0);
    if (pos1) resizeObserver.observe(pos1);
    if (track) resizeObserver.observe(track);
    resizeObserver.observe(wrapper);

    const mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(wrapper, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [
    itemIdx,
    registerPos0Height,
    registerPos1Height,
    comboSettings.timelineContentPlacement,
    comboSettings.timelinePosition,
    comboSettings.timelineMarkerShape,
  ]);

  const dynamicMiddlePadding = Math.max(34, trackMaxHeight / 2 + 12);

  // Track each side's max height independently so an item with a tall
  // group below + short group above doesn't pad the short side to the
  // tall side's height (component would otherwise be 2 × max).
  const maxPos0Height = maxHeightInRange(pos0Heights, totalAmtOfItems);
  const maxPos1Height = maxHeightInRange(pos1Heights, totalAmtOfItems);

  const isNewLine =
    isHorizontal &&
    comboSettings.timelineContentWrapping ===
      TimelineContentWrappingEnum.NewLine;

  const itemDetails = useTimelineItemDetails({
    itemIdx,
  });
  const { isOutside, isLastInView, isContentOnLeft, contentWidth } =
    itemDetails;

  const { isItemVisible, trackSvgData } = useTimelineItemAnim({
    itemRef,
    itemIdx,
  });

  // Horizontal NewLine bypasses scroll-based active dimming — every item
  // stays in the active state regardless of viewport position.
  const isAlwaysActive = isHorizontal && isNewLine;
  const isItemActive = isAlwaysActive || isItemVisible;

  const handleItemClick = (e: MouseEvent) => {
    if (isMobile) {
      const swiperSlide = e.currentTarget.closest(".swiper-slide");
      if (swiperSlide instanceof HTMLElement) {
        swiperSlide.scrollIntoView();
      }
      return;
    }
    moveToItemIdx(itemIdx);
  };

  const trackSegment = isHorizontal ? (
    <TimelineHorizontalTrackSegment
      itemDetails={itemDetails}
      itemIdx={itemIdx}
      trackSvgData={trackSvgData}
      setSettings={setSettings}
      editableMarkerSlot={editableMarkerSlot}
      editableTextSlot={editableTextSlot}
    />
  ) : null;

  const contentBlock = (
    <div
      ref={itemRef}
      data-is-last-in-view={isLastInView}
      data-item-active={isItemActive}
      data-is-outside={isOutside}
      className={classNames({
        "tw-flex": true,
        "timeline-item": true,
        // Vertical
        "tw-w-full tw-text-left": isVertical,
        "tw-mt-4": isVertical && itemIdx > 0,
        "tw-justify-start": isVertical && isContentOnLeft,
        "tw-justify-end": isVertical && !isContentOnLeft,
        // Horizontal
        "tw-h-full": isHorizontal && !isNewLine,
        "tw-opacity-0":
          isHorizontal &&
          !isNewLine &&
          !isDragged &&
          !isPdf &&
          !isMobile &&
          isOutside,
      })}
      onClick={
        isOutside && isHorizontal && !isNewLine ? handleItemClick : undefined
      }
    >
      {isVertical && (
        <TimelineVerticalTrackSegment
          itemDetails={itemDetails}
          itemIdx={itemIdx}
          trackSvgData={trackSvgData}
          setSettings={setSettings}
          editableMarkerSlot={editableMarkerSlot}
          editableTextSlot={editableTextSlot}
        />
      )}

      <div
        className={classNames({
          "timeline-item-content": true,
          "tw-h-full": isHorizontal && !isNewLine,
        })}
        style={{
          width: contentWidth,
        }}
      >
        {children}
      </div>
    </div>
  );

  const contentPlacement = isHorizontal
    ? (comboSettings.timelineContentPlacement ?? "default")
    : "default";

  // Alternating outputs 3 grid children per item — above / track / below
  // — so every item's track aligns on the same row across the grid. In
  // new-line mode items wrap to additional row-blocks of 3 rows each.
  if (isHorizontal && contentPlacement === "alternating") {
    let perRow: number | null = null;
    if (isNewLine) {
      const setsPerSlide = comboSettings.timelineSetsPerSlide;
      const isAuto = !setsPerSlide || setsPerSlide === "auto";
      perRow = isAuto ? 3 : Number(setsPerSlide);
    }

    // NewLine wraps onto stacked rows, so alternation restarts at every
    // row boundary — the first item of each wrapped row sits above the
    // line. The carousel is a single continuous line, so it alternates by
    // global index instead; restarting per page would put two adjacent
    // items on the same side at each page boundary. Must match the marker
    // peak/trough in timeline-horizontal-track-segment.tsx.
    const isContentAbove = perRow
      ? (itemIdx % perRow) % 2 === 0
      : itemIdx % 2 === 0;

    const rowBlock = perRow ? Math.floor(itemIdx / perRow) : 0;
    const col = perRow ? (itemIdx % perRow) + 1 : itemIdx + 1;
    const baseRow = rowBlock * 3;

    return (
      <>
        <div
          data-alt-slot="above"
          className="tw-flex tw-flex-col tw-justify-end"
          style={{ gridColumn: col, gridRow: baseRow + 1 }}
        >
          {isContentAbove ? contentBlock : null}
        </div>
        <div
          data-alt-slot="track"
          className="tw-flex tw-items-center"
          style={{ gridColumn: col, gridRow: baseRow + 2 }}
        >
          {trackSegment}
        </div>
        <div
          data-alt-slot="below"
          className="tw-flex tw-flex-col tw-justify-start"
          style={{ gridColumn: col, gridRow: baseRow + 3 }}
        >
          {!isContentAbove ? contentBlock : null}
        </div>
      </>
    );
  }

  // Split: track renders above (top), below (bottom), or between two
  // content groups (middle, with measured pos-0 / pos-1 heights).
  if (isHorizontal && contentPlacement === "split") {
    const position = comboSettings.timelinePosition;

    if (position === "top" || position === "bottom") {
      return (
        <div className="tw-flex tw-h-full tw-flex-col">
          {position === "bottom" ? (
            <>
              <div className="tw-mb-4 tw-flex-1">{contentBlock}</div>
              {trackSegment}
            </>
          ) : (
            <>
              {trackSegment}
              <div className="tw-mt-4 tw-flex-1">{contentBlock}</div>
            </>
          )}
        </div>
      );
    }

    return (
      <div
        ref={splitMiddleRef}
        className="tw-relative"
        data-timeline-middle-full
        style={
          {
            ...splitMiddleCardVars(setSettings),
            "--timeline-middle-padding": `${dynamicMiddlePadding}px`,
            "--timeline-pos0-min-height":
              maxPos0Height !== null ? `${maxPos0Height}px` : undefined,
            "--timeline-pos1-min-height":
              maxPos1Height !== null ? `${maxPos1Height}px` : undefined,
          } as React.CSSProperties
        }
      >
        {contentBlock}
        <div
          className="tw-absolute tw-inset-x-0 tw-flex tw-flex-col tw-justify-center"
          style={{
            top: lineTop !== null ? lineTop : "50%",
            transform: "translateY(-50%)",
            height: "calc(var(--timeline-middle-padding, 34px) * 2)",
            zIndex: 11,
          }}
        >
          {trackSegment}
        </div>
      </div>
    );
  }

  // Snake order: items flow LTR on even rows and RTL on odd rows via
  // boustrophedon column placement. The pl-10 / pr-10 padding leaves
  // room for the corner-radius=40 bends at each row end so the curve
  // doesn't hit the item content.
  if (isHorizontal && comboSettings.timelineSnakeOrder) {
    const setsPerSlide = comboSettings.timelineSetsPerSlide;
    const perRow =
      !setsPerSlide || setsPerSlide === "auto" ? 3 : Number(setsPerSlide);
    const rowIdx = Math.floor(itemIdx / perRow);
    const posInRow = itemIdx % perRow;
    const col = rowIdx % 2 === 0 ? posInRow + 1 : perRow - posInRow;
    const isBottom = comboSettings.timelinePosition === "bottom";
    // Fixed min-height so swapping marker types doesn't jump the row's
    // content/track vertical split.
    const trackSlot = (
      <div className="tw-flex tw-items-center" style={{ minHeight: 80 }}>
        {trackSegment}
      </div>
    );
    return (
      <div
        className="tw-flex tw-h-full tw-flex-col tw-pl-10 tw-pr-10"
        style={{ gridColumn: col, gridRow: rowIdx + 1 }}
      >
        {isBottom ? (
          <>
            <div className="tw-flex-1">{contentBlock}</div>
            {trackSlot}
          </>
        ) : (
          <>
            {trackSlot}
            <div className="tw-flex-1">{contentBlock}</div>
          </>
        )}
      </div>
    );
  }

  // h-path: marker sits at the right edge as a between-items separator.
  // Renders TimelineMarkerFactory directly to skip the track-segment's
  // vertical ordering / margin classes that don't apply in flex-row.
  // Checked before isNewLine so carousel + new-line share this layout.
  if (isHorizontal && comboSettings.timelineHideLine) {
    return (
      <div className="tw-flex tw-h-full tw-flex-row tw-items-center">
        <div className="tw-min-w-0 tw-flex-1">{contentBlock}</div>
        <div
          className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center"
          // maxWidth caps the marker column so a long custom-text label
          // can't squeeze the content column to one char per line.
          style={{ minWidth: 80, maxWidth: 120 }}
        >
          <TimelineMarkerFactory
            setSettings={setSettings}
            itemIdx={itemIdx}
            editableMarkerSlot={editableMarkerSlot}
            editableTextSlot={editableTextSlot}
          />
        </div>
      </div>
    );
  }

  if (isNewLine) {
    const isBottom = comboSettings.timelinePosition === "bottom";
    return (
      <div className="tw-flex tw-h-full tw-flex-col">
        {isBottom ? (
          <>
            <div className="tw-flex-1">{contentBlock}</div>
            {trackSegment}
          </>
        ) : (
          <>
            {trackSegment}
            <div className="tw-flex-1">{contentBlock}</div>
          </>
        )}
      </div>
    );
  }

  // Wide chevron in Middle: reuses the split-middle ref + measurement so
  // the chevron sits between pos-0 / pos-1 (boundary between two content
  // groups), not the geometric middle of the component.
  if (
    isHorizontal &&
    comboSettings.timelineMarkerShape === "arrow-big" &&
    comboSettings.timelinePosition === "middle"
  ) {
    return (
      <div
        ref={splitMiddleRef}
        className="tw-relative"
        data-timeline-middle-full
        style={
          {
            ...splitMiddleCardVars(setSettings),
            "--timeline-middle-padding": "56px",
            "--timeline-pos0-min-height":
              maxPos0Height !== null ? `${maxPos0Height}px` : undefined,
            "--timeline-pos1-min-height":
              maxPos1Height !== null ? `${maxPos1Height}px` : undefined,
          } as React.CSSProperties
        }
      >
        {contentBlock}
        <div
          className="tw-absolute tw-inset-x-0 tw-flex tw-flex-col tw-justify-center"
          style={{
            top: lineTop !== null ? lineTop : "50%",
            transform: "translateY(-50%)",
            height: "calc(var(--timeline-middle-padding, 34px) * 2)",
            zIndex: 11,
          }}
        >
          {trackSegment}
        </div>
      </div>
    );
  }

  return (
    <>
      {trackSegment}
      {contentBlock}
    </>
  );
};
